import { contestDevice, device } from '../layouts/device';
import { BDSColor } from '../tokens/color';
import { BDSFont } from '../tokens/font';
import { hexToRgba } from '../utils';

/**
 * chat / affiliate 에서 공통적으로 사용되는 theme
 * text, line, icon, static
 */
export const commonTheme = {
  ...BDSFont,
  device,
  palette: BDSColor,
  color: {
    static: {
      white: {
        role: '',
        light: BDSColor.common.white,
        dark: BDSColor.common.white,
      },
      black: {
        role: '',
        light: BDSColor.common.black,
        dark: BDSColor.common.black,
      },
      primary: {
        role: '',
        light: BDSColor.aquamarine[50],
        dark: BDSColor.aquamarine[50],
      },
      secondary: {
        role: '',
        light: BDSColor.purple[50],
        dark: BDSColor.purple[50],
      },
      warning: {
        role: '',
        light: BDSColor.pink[50],
        dark: BDSColor.pink[50],
      },
      red: {
        role: '',
        light: BDSColor.red[50],
        dark: BDSColor.red[50],
      },
      pink: {
        role: '',
        light: BDSColor.pink[50],
        dark: BDSColor.pink[50],
      },
      adult: {
        role: '',
        light: BDSColor.red[50],
        dark: BDSColor.red[50],
      },
    },
    text: {
      default: {
        role: '기본 텍스트 색상',
        light: BDSColor.common.black,
        dark: BDSColor.common.white,
      },
      default_2: {
        role: '기본 텍스트 색상 2',
        light: BDSColor.common.white,
        dark: BDSColor.common.black,
      },
      TXT_0: {
        role: '중요도가 낮은 텍스트 색상',
        sub_description: 'Input Field의 가이드 텍스트 컬러',
        light: BDSColor.neutral[30],
        dark: BDSColor.neutral[38],
      },
      TXT_1: {
        role: '텍스트가 밀집된 영역에서 중요도가 낮은 텍스트의 컬러',
        sub_description: 'Info text, sub text',
        light: BDSColor.neutral[38],
        dark: BDSColor.neutral[30],
      },
      TXT_2: {
        role: '본문 텍스트에 사용되는 색상',
        sub_description: '본문',
        light: BDSColor.neutral[55],
        dark: BDSColor.neutral[18],
      },
      TXT_3: {
        role: '본문보다 상위 레벨의 텍스트에 사용되는 색상',
        sub_description: '메뉴 타이틀 등',
        light: BDSColor.neutral[55],
        dark: BDSColor.neutral[25],
      },
      TXT_4: {
        role: '페이지에서 상위 레벨의 중요도를 갖는 텍스트에 사용되는 색상',
        sub_description: '타이틀',
        light: BDSColor.neutral[70],
        dark: BDSColor.neutral[8],
      },
      info: {
        role: 'Info',
        sub_description: 'AI 모델 선택 모달 > 사용 중, 버블 표시 텍스트',
        light: '#229BDC',
        dark: '#229BDC',
      },
    },
    line: {
      line_1: {
        role: 'Text',
        sub_description: 'Text',
        light: BDSColor.variant[15],
        dark: BDSColor.neutral[65],
      },
      line_2: {
        role: 'Text',
        sub_description: 'Text',
        light: BDSColor.variant[25],
        dark: BDSColor.neutral[55],
      },
      line_3: {
        role: 'Text',
        sub_description: 'Text',
        light: BDSColor.neutral[20],
        dark: BDSColor.cool[55],
      },
    },
    // 레거시 호환용 색상 (UI 컴포넌트에서 사용)
    legacy: {
      gray: {
        role: 'Legacy gray color',
        light: '#d8d8d8',
        dark: '#4a4a4a',
      },
      darkgray: {
        role: 'Legacy darkgray color',
        light: '#bcbcbc',
        dark: '#6a6a6a',
      },
      brownishGray: {
        role: 'Legacy brownishGray color',
        light: '#666666',
        dark: '#999999',
      },
      red: {
        role: 'Legacy red color',
        light: '#ff4444',
        dark: '#ff6666',
      },
    },
    // item 호버 색상
    item: {
      list_hover: {
        role: 'List item hover background',
        light: 'rgba(0, 0, 0, 0.05)',
        dark: 'rgba(255, 255, 255, 0.1)',
      },
      lb_aquamarine: {
        role: '라벨 bg 색상(LLM 모델 라벨)',
        light: hexToRgba(BDSColor.aquamarine[50], 0.08),
        dark: hexToRgba(BDSColor.aquamarine[50], 0.08),
      },
      highlight: {
        role: '충전소 내 강조 버튼, 텍스트 색상',
        light: BDSColor.cobalt_blue[50],
        dark: BDSColor.cobalt_blue[50],
      },
    },
    icon: {
      icon_1: {
        role: '타이틀과 동일 라인에 사용하는 색상, 타이틀보다 중요도 낮음',
        sub_description: '헤더 메뉴, 서치 아이콘 등',
        light: BDSColor.neutral[30],
        dark: BDSColor.neutral[48],
      },
      icon_2: {
        role: '버튼형 아이콘의 메인 색상',
        sub_description: 'Text',
        light: BDSColor.neutral[38],
        dark: BDSColor.neutral[38],
      },
      icon_3: {
        role: '버튼형 아이콘 컬러, icon-2보다 더 중요한 아이콘 색상',
        sub_description: '대화방 편집/카메라 버튼, 커뮤니케이션 버튼',
        light: BDSColor.neutral[55],
        dark: BDSColor.neutral[20],
      },
      icon_4: {
        role: '주요 기능 아이콘 색상',
        sub_description: 'APP 메인 화면, 대화방 사이드 메뉴 아이콘 등',
        light: BDSColor.cool[60],
        dark: BDSColor.variant[15],
      },
    },
  },
  contestDevice,
} as const;
